import { getTensorData } from '../utils/tensorUtils.js';
import { computePolygonBbox } from '../../utils/polygonMerge.js';

/** Default logit threshold for binarization (foreground when value > threshold). Matches Python predictor default. */
const MASK_THRESHOLD = 0.0;

/** Default logit threshold for binarization (foreground when value > threshold). */
const DEFAULT_MASK_THRESHOLD = 0;
/** Minimum contour area (pixels) to keep. */
const DEFAULT_MIN_AREA = 10;
/** Epsilon for approxPolyDP as ratio of contour perimeter. */
const DEFAULT_SIMPLIFY_EPS_RATIO = 0.003;

/**
 * Binarize logits with a threshold (matches Python "masks > self.mask_threshold").
 * @param {Float32Array|Uint8Array} logits - Logit values at each pixel
 * @param {number} [maskThreshold=0.0] - Foreground when value > threshold
 * @returns {Uint8Array} Binary mask (0 or 1)
 */
export function getBinaryMask(logits, maskThreshold = MASK_THRESHOLD) {
    const binary = new Uint8Array(logits.length);
    for (let i = 0; i < logits.length; i++) {
        binary[i] = logits[i] > maskThreshold ? 1 : 0;
    }
    return binary;
}

/**
 * Extract the best mask from decoder output and binarize it.
 * ONNX decoder outputs masks already at orig_im_size (no resize needed).
 * Matches Python: binary = (decoder masks logits) > mask_threshold.
 *
 * @param {Object} decoderResults - { masks: Tensor, iou_predictions: Tensor }
 * @param {Array<number>} origImSize - [height, width] (matches orig_im_size tensor order)
 * @param {number|null} [bestMaskIndex=null] - If provided, use this mask; otherwise pick by max IoU
 * @param {number} [maskThreshold=0.0]
 * @returns {{
 *   binary: Uint8Array,
 *   logits: Float32Array,
 *   shape: [number, number],
 *   bestMaskIndex: number|null,
 *   iouScores: Array<number>
 * }} binary mask, logits for best mask, [h, w], selected index, and IoU scores
 */
export function getBinaryMaskFromDecoderOutput(decoderResults, origImSize, bestMaskIndex = null, maskThreshold = MASK_THRESHOLD) {
    const masksTensor = decoderResults?.masks;
    const iouTensor = decoderResults?.iou_predictions;
    if (!masksTensor?.dims || !iouTensor) {
        const [h, w] = origImSize;
        const size = (h || 0) * (w || 0) || 1;
        return {
            binary: new Uint8Array(size).fill(0),
            logits: new Float32Array(size),
            shape: [h || 0, w || 0],
            bestMaskIndex: null,
            iouScores: [],
        };
    }

    // maskData = [(logits values of mask 0), (logits values of mask 1), (logits values of mask 2)]
    const masksData = getTensorData(masksTensor);
    const iouData = getTensorData(iouTensor);
    if (!masksData) {
        const [h, w] = origImSize;
        const size = h * w;
        return {
            binary: new Uint8Array(size).fill(0),
            logits: new Float32Array(size),
            shape: [h, w],
            bestMaskIndex: null,
            iouScores: iouData ? Array.from(iouData, Number) : [],
        };
    }

    const dims = masksTensor.dims;
    const numMasks = dims.length >= 2 ? dims[1] : 1;
    const [h, w] = origImSize;
    const maskSize = h * w;

    const iouScores = iouData ? Array.from(iouData, Number) : [];
    const autoBestIndex = (() => {
        if (!iouScores.length) return 0;
        const maxVal = Math.max(...iouScores);
        return iouScores.indexOf(maxVal);
    })();
    const index = bestMaskIndex != null
        ? Math.max(0, Math.min(bestMaskIndex, numMasks - 1))
        : autoBestIndex;

    const start = index * maskSize;
    const end = start + maskSize;
    const logits = masksData.slice ? masksData.slice(start, end) : new Float32Array(masksData.buffer, masksData.byteOffset + start * 4, maskSize);
    const logitsArr = logits instanceof Float32Array ? logits : new Float32Array(logits);
    const binary = getBinaryMask(logitsArr, maskThreshold);

    return { binary, logits: logitsArr, shape: [h, w], bestMaskIndex: index, iouScores };
}


/**
 * Convert a single binary mask (at original size) to polygon rings and bbox using OpenCV.
 * Aligned with processor.polygonToMaskTensor:
 * - Same coordinate space: (height, width) = (rows, cols), mask row-major index = row * width + col.
 * - Contour points from findContours are (x, y) = (col, row), matching polygon points used in fillPoly.
 * - Returned polygon is single ring [[x,y],...] or rings [[[x,y],...], ...], suitable for polygonToMaskTensor.
 * Limitation: RETR_EXTERNAL returns only outer contours, so holes in the mask are lost (outer boundary only).
 * @param {typeof import('@techstark/opencv-js')} cv
 * @param {Uint8Array} maskData
 * @param {number} height
 * @param {number} width
 * @param {Object} [options]
 * @param {number} [options.minArea=10]
 * @param {number} [options.simplifyEpsRatio=0.003]
 * @returns {{ polygon: Array, bbox: Array<number> }}
 */
function maskToPolygon(cv, maskData, height, width, options = {}) {
    const minArea = options.minArea ?? DEFAULT_MIN_AREA;
    const simplifyEpsRatio = options.simplifyEpsRatio ?? DEFAULT_SIMPLIFY_EPS_RATIO;

    const mat = new cv.Mat(height, width, cv.CV_8UC1);
    for (let i = 0; i < height * width; i++) {
        mat.data[i] = maskData[i] > 0 ? 255 : 0;
    }

    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(mat, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
    mat.delete();

    const rings = [];
    const nContours = contours.size();
    for (let c = 0; c < nContours; c++) {
        const contour = contours.get(c);
        if (cv.contourArea(contour) < minArea) {
            contour.delete();
            continue;
        }
        const perimeter = cv.arcLength(contour, true);
        const eps = simplifyEpsRatio * perimeter;
        const approx = new cv.Mat();
        cv.approxPolyDP(contour, approx, eps, true);
        contour.delete();
        const pts = contourPointsFromMat(approx);
        approx.delete();
        if (pts.length >= 3) {
            rings.push(pts.map(([x, y]) => [Math.round(Number(x)), Math.round(Number(y))]));
        }
    }
    contours.delete();
    hierarchy.delete();

    let bbox = computePolygonBbox(rings);

    const polygon = rings.length === 1 ? rings[0] : rings;
    return { polygon, bbox };
}

/**
 * Convert a contour Mat to an array of points.
 * @param {typeof import('@techstark/opencv-js').Mat} contour
 * @returns {Array<[number, number]>} points [[x,y],...]
 */
function contourPointsFromMat(contour) {
    const pts = [];
    const data = contour.data32S;
    if (!data || data.length < 2) return pts;
    const numPoints = contour.rows * contour.cols;
    for (let i = 0; i < numPoints && 2 * i + 1 < data.length; i++) {
        pts.push([data[2 * i], data[2 * i + 1]]);
    }
    return pts;
}

/**
 * Full post-process: select best mask by IoU, binarize (decoder output is already at orig_im_size),
 * then convert the binary mask to polygon + bbox.
 *
 * @param {typeof cv} cv
 * @param {Object} decoderResults - { masks, iou_predictions }
 * @param {Array<number>} original_sizes - Original image size [height, width] (matches decoder orig_im_size tensor order)
 * @param {Object} [options] - maskThreshold, minArea, simplifyEpsRatio
 * @returns {{ polygon: Array, bbox: Array<number> }}
 */
export default function postProcess(cv, decoderResults, original_sizes, options = {}) {
    const { binary, shape } = getBinaryMaskFromDecoderOutput(
        decoderResults,
        original_sizes,
        null,
        options.maskThreshold ?? DEFAULT_MASK_THRESHOLD
    );
    const [height, width] = shape;
    if (!height || !width || binary.length !== height * width) {
        return { polygon: [], bbox: [] };
    }
    return maskToPolygon(cv, binary, height, width, options);
}
